
// 2.2 Supabase Database Schema Mappings & Enums

export enum EntityStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export enum HabitWeight {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 4,
}

export enum GoalType {
  TASK_PROJECT = 'task_project',
  CONSISTENCY_METRIC = 'consistency_metric',
}

export enum GoalStatus {
  ACTIVE = 'active',
  DONE = 'done',
  ABANDONED = 'abandoned',
}

export enum ArchiveType {
  ROUTINE = 'routine',
  VISION_5Y = 'vision_5y',
  LIFE_TODO = 'life_todo',
  THEORY_NOTES = 'theory_notes',
}

// -- CORE MANAGEMENT --
export interface Profile {
  id: string;
  current_version_id: string | null;
  current_cycle_id: string | null;
  avatar_url?: string | null;
  username: string;
  subscription_plan: 'free' | 'pro';
  has_onboarded: boolean;
  has_toured: boolean;
  onboarding_step: string | null;
  // System Tempo Settings
  version_days_default: number; // Default 90
  cycles_per_version_default: number; // Default 6
  settings?: UserSettings;
}

// -- TIME MANAGEMENT --
export interface Version {
  id: string;
  number: number;
  title: string;
  description: string; // The "Persona"
  status: EntityStatus;
  start_date: string; // ISO Date
  end_date: string; // ISO Date
}

export interface CCHItem {
  category: 'Characteristics' | 'Skills';
  items: string[];
}

export interface IdentityItem {
  id: string;
  user_id?: string;
  name: string;
  type: 'skill' | 'characteristic';
  created_at?: string;
}

export interface Cycle {
  id: string;
  version_id: string;
  sprint_number: number;
  focus_priorities: string[]; // Max 5
  problems: string[];
  cch_list: CCHItem[];
  mantras: string[];
  learning_focus: string;
  start_date: string;
  end_date: string;
  status?: EntityStatus;
  debrief?: CycleDebrief | null;
}

// -- ACTION ENGINE --
export interface Habit {
  id: string;
  cycle_id: string;
  name: string;
  weight: HabitWeight;
  category: string;
  linked_goal_id?: string;
  sort_order?: number;
}

export interface HabitLog {
  id: string;
  habit_id: string;
  date: string; // ISO Date "YYYY-MM-DD"
  status: boolean;
}

export interface Subtask {
  name: string;
  done: boolean;
  level?: number; // 0 = root, 1 = sub, etc.
}

export type GoalPriority = 'low' | 'medium' | 'high';
export type GoalRiskLevel = 'low' | 'medium' | 'high';

export interface Goal {
  id: string;
  cycle_id: string;
  title: string;
  description: string;
  type: GoalType;
  status?: GoalStatus;
  priority?: GoalPriority;
  goal_lineage_id?: string;
  // For Projects
  subtasks?: Subtask[];
  // For Metrics
  linked_habit_id?: string;
  current_streak?: number;
  sort_order?: number;
  // derived (0-1): completed/elapsed ratio over the cycle window, attached by
  // enrichGoalsConsistency; NOT persisted.
  consistency?: number;
}

// -- KNOWLEDGE & ARCHIVES --
export interface ArchiveItem {
  id: string;
  type: ArchiveType;
  title: string;
  content: string;
  created_at?: string;
}

export interface Mantra {
  id: string;
  user_id?: string;
  text: string;
  created_at?: string;
}

// -- VISUALIZATION --
export interface Slide {
  id: string;
  /** User-authored. Empty until they name the slide; never derived from a category. */
  title: string;
  /** User-authored body. Successor to `caption`. */
  description: string;
  image_url: string;
  /** Sort order within the user's cinema, 0-based. */
  position: number;
}

// -- NOTIFICATION SYSTEM --
export type NotificationPriority = 'low' | 'medium' | 'high';
export type NotificationType = 'time' | 'achievement' | 'debrief' | 'pulse';

export type NotificationActionType =
  | 'debrief' | 'create-cycle' | 'create-version'
  | 'pulse-morning' | 'pulse-evening' | 'view-report';

export interface AppNotification {
  id: string;
  notificationKey: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  icon: string;
  timestamp: string;
  actionLabel?: string;
  actionType?: NotificationActionType;
}

// -- DB-BACKED NOTIFICATIONS --

/** Raw row from the `notifications` table */
export interface DBNotification {
  id: string;
  user_id: string;
  notification_key: string;
  type: NotificationType;
  priority: NotificationPriority;
  title_key: string;
  message_key: string;
  params: Record<string, unknown>;
  icon: string;
  action_type: NotificationActionType | null;
  action_label_key: string | null;
  created_at: string;
  dismissed_at: string | null;
  expires_at: string | null;
}

/** What computeNotificationSpecs() returns — no translation, no DB fields */
export interface NotificationSpec {
  notification_key: string;
  type: NotificationType;
  priority: NotificationPriority;
  title_key: string;
  message_key: string;
  params: Record<string, unknown>;
  icon: string;
  action_type?: NotificationActionType;
  action_label_key?: string;
  expires_at?: string; // YYYY-MM-DD
}

// -- PULSE SETTINGS --
export interface PulseSettings {
  morning_reminder_enabled?: boolean;
  morning_reminder_time?: string;
  evening_reminder_enabled?: boolean;
  evening_reminder_time?: string;
}

// -- USER SETTINGS (JSONB on profiles) --
export interface UserSettings {
  language?: string;
  timezone?: string;
  auth?: {
    has_password_linked?: boolean;
  };
  avatar?: {
    removed?: boolean;
  };
  display?: {
    show_shortcut_hints?: boolean;
    sidebar_collapsed?: boolean;
  };
  features?: {
    identity_system_enabled?: boolean;
  };
  pulse?: PulseSettings;
  insights?: InsightsSettings;
  notifications?: NotificationPrefs;
  email?: EmailSettings;
}

export interface CycleDebrief {
  completed_at: string;
  avg_daily_score: number;
  perfect_days: number;
  total_days: number;
  best_habits: { name: string; consistency: number }[];
  worst_habits: { name: string; consistency: number }[];
  goals_completed: number;
  goals_total: number;
  streak_highlights: { habitName: string; streak: number }[];
  reflection: string;
}

export interface PulseCheckin {
  id: string;
  user_id?: string;
  date: string; // YYYY-MM-DD
  // Morning
  sleep_bed_time: string | null;
  sleep_wake_time: string | null;
  sleep_quality: number | null;
  morning_mood: number | null;
  morning_energy: number | null;
  morning_feelings: string[] | null;
  one_thing: string | null;
  morning_completed_at: string | null;
  focus_tags: string[] | null;
  // Evening
  evening_mood: number | null;
  evening_performance: number | null;
  evening_stress: number | null;
  evening_motivation: number | null;
  evening_energy: number | null;
  evening_connectedness: number | null;
  evening_feelings: string[] | null;
  one_thing_completed: boolean | null;
  activity_tags: string[] | null;
  reflection: string | null;
  evening_completed_at: string | null;
  reflection_sentiment?: number | null;  // 0-100, LLM-derived (Tier 2)
}

export interface PulseInsights {
  id?: string;
  computed_at: string;
  data_days: number;
  period_start: string;
  period_end: string;
  mood_label: string | null;
  insights: StoredInsight[];
  summary?: string | null;
}

// -- INSIGHT ENGINE TYPES --

export type InsightCategory = "sleep" | "activity" | "habit" | "rhythm" | "lag" | "pattern" | "regression" | "blindspot" | "feeling" | "kpi";
export type InsightStrength = "low" | "moderate" | "strong";

export interface StoredInsight {
  id: string;
  category: InsightCategory;
  polarity: "positive" | "negative";
  strength: InsightStrength;
  lift_pct: number;
  factor: string;
  target: string;
  first_detected_at: string;
  last_updated_at: string;
  notified_at: string | null;
  n?: number;
}

export interface InsightsSettings {
  enabled?: boolean;
  include_habits?: boolean;
  report_frequency?: "biweekly" | "monthly" | "bimonthly" | "quarterly" | "per_cycle";
  sentiment_enabled?: boolean;        // Tier 2: LLM-derived reflection sentiment
  ai_narratives_enabled?: boolean;    // Tier 3: AI-generated weekly summaries
}

export interface NotificationPrefs {
  new_insights?: boolean;
  cycle_deadlines?: boolean;
  version_deadlines?: boolean;
  streak_milestones?: boolean;
  goal_completions?: boolean;
  debrief_ready?: boolean;
  no_active_cycle?: boolean;
}

export interface EmailSettings {
  weekly_digest?: boolean;
  digest_day?: number;
}

export interface DailyData {
  date: string;
  mood: number | null;
  energy: number | null;
  stress: number | null;
  performance: number | null;
  motivation: number | null;
  connectedness: number | null;
  sleep_hours: number | null;
  sleep_quality: number | null;
  focus_tags: string[];
  activity_tags: string[];
  habits_completed: string[];
  feelings: string[];
  /** Optional daily score 0-100 from habit-weighted completion. Set by callers that have it. */
  daily_score?: number;
}

// -- TRENDS CHART DATA TYPES --
export interface MoodDay {
  date: string; // YYYY-MM-DD
  mood: number | null; // internal 1-10 scale, never displayed to user
}

export interface VitalDataPoint {
  date: string;
  value: number; // 1-10
}

export interface EmotionFrequency {
  feeling: string;
  count: number;
  sentiment: "positive" | "neutral" | "negative";
}

export interface EmotionTrendPoint {
  date: string;
  [feeling: string]: number | string;
}

// -- DATA TRANSFER OBJECTS FOR UI --
export interface DashboardData {
  version: Version | null;
  cycle: Cycle | null;
  habits: Habit[];
  todayLogs: HabitLog[];
  habitStats: Record<string, { currentStreak: number; maxStreak: number }>;
  goals: Goal[];
  lastEndedCycle: { id: string; sprint_number: number; version_number: number; hasDebrief: boolean } | null;
}

// -- API KEYS --
export interface ApiKeyPermissions {
  portal_snapshot?: boolean;
  portal_review?: boolean;
  portal_history?: boolean;
  portal_search?: boolean;
  portal_log_habits?: boolean;
  portal_create_cycle?: boolean;
  portal_debrief_cycle?: boolean;
  portal_update?: boolean;
  portal_create?: boolean;
  portal_delete?: boolean;
  portal_pulse?: boolean;
  portal_settings?: boolean;
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  permissions: ApiKeyPermissions;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
